import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useWorkoutsPaginated } from '../hooks/useWorkouts';
import { WorkoutCreate, WorkoutFilters, Client } from '../types';
import { Calendar, Clock, Users, Search, Plus, Eye, Trash2, CheckCircle, X, Share2 } from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import toast from 'react-hot-toast';
import CreateWorkoutModal from '../components/CreateWorkoutModal';
import ViewClientModal from '../components/ViewClientModal';
import WeightModal from '../components/WeightModal';
import BodyMeasurementsModal from '../components/BodyMeasurementsModal';
import ClientPhotoModal from '../components/ClientPhotoModal';
import Navigation from '../components/Navigation';
import Pagination from '../components/Pagination';
import { useQueryClient } from 'react-query';
import apiService from '../services/api';

const WorkoutsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Состояние пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending'>('all');
  const [isCreateWorkoutModalOpen, setIsCreateWorkoutModalOpen] = useState(false);
  const [packageConsumptions, setPackageConsumptions] = useState<{[workoutId: number]: any[]}>({});
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isViewClientModalOpen, setIsViewClientModalOpen] = useState(false);

  // Состояния для модальных окон клиента
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [selectedClientForWeight, setSelectedClientForWeight] = useState<Client | null>(null);
  const [isBodyMeasurementsModalOpen, setIsBodyMeasurementsModalOpen] = useState(false);
  const [selectedClientForBodyMeasurements, setSelectedClientForBodyMeasurements] = useState<Client | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedClientForPhoto, setSelectedClientForPhoto] = useState<Client | null>(null);

  const itemsPerPage = 10; // Количество тренировок на странице
  
  // Создаем фильтры для API
  const filters: WorkoutFilters = useMemo(() => {
    const apiFilters: WorkoutFilters = {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
    };
    
    // Добавляем поиск если есть
    if (searchTerm.trim()) {
      apiFilters.search = searchTerm.trim();
    }
    
    // Добавляем фильтр по статусу
    if (statusFilter !== 'all') {
      apiFilters.status = statusFilter;
    }
    
    return apiFilters;
  }, [currentPage, itemsPerPage, searchTerm, statusFilter]);
  
  const { 
    workouts = [], 
    meta,
    isLoading, 
    deleteWorkout, 
    isDeleteLoading,
    completeWorkout,
    isCompleteLoading,
    createWorkoutWithCallback,
    isCreateLoading: isCreateWorkoutLoading
  } = useWorkoutsPaginated(filters);

  // Вычисляем общее количество страниц
  const totalCount = meta?.total || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Обработчик изменения страницы
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Прокручиваем наверх страницы при смене страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Сброс на первую страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Инициализация фильтра из URL параметров
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');
    if (statusParam === 'completed' || statusParam === 'pending') {
      setStatusFilter(statusParam);
    }
  }, [location.search]);

  // Обновление URL при изменении фильтра
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (statusFilter === 'all') {
      urlParams.delete('status');
    } else {
      urlParams.set('status', statusFilter);
    }
    
    const newUrl = urlParams.toString() ? `?${urlParams.toString()}` : '/workouts';
    if (location.search !== `?${urlParams.toString()}`) {
      navigate(newUrl, { replace: true });
    }
  }, [statusFilter, navigate, location.search]);

  // Теперь workouts уже приходят отсортированными с сервера

  // Обработчик создания тренировки
  const handleCreateWorkout = (data: WorkoutCreate) => {
    console.log('WorkoutsPage: Creating workout with data:', data);
    createWorkoutWithCallback(data, (workout) => {
      console.log('WorkoutsPage: Workout created successfully:', workout);
      setIsCreateWorkoutModalOpen(false);
      navigate(`/workouts/${workout.id}`);
    });
  };

  // Обработчик удаления тренировки
  const handleDeleteWorkout = async (workoutId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту тренировку?')) {
      try {
        await deleteWorkout(workoutId);
        toast.success('Тренировка удалена успешно');
      } catch (error) {
        toast.error('Ошибка при удалении тренировки');
      }
    }
  };

  // Обработчик завершения тренировки
  const handleCompleteWorkout = async (workoutId: number) => {
    if (window.confirm('Вы уверены, что хотите завершить эту тренировку?')) {
      try {
        await completeWorkout(workoutId);
        toast.success('Тренировка завершена успешно');
      } catch (error) {
        toast.error('Ошибка при завершении тренировки');
      }
    }
  };

  // Обработчик очистки поиска
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Автоматическое обновление данных при изменении ссылок
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'share_link_updated') {
        // Обновляем данные тренировок при изменении ссылок
        queryClient.invalidateQueries('workouts');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [queryClient]);

  // Загружаем информацию о потреблении пакетов для каждой тренировки
  useEffect(() => {
    const loadPackageConsumptions = async () => {
      if (workouts && workouts.length > 0) {
        const consumptions: {[workoutId: number]: any[]} = {};

        for (const workout of workouts) {
          try {
            const consumption = await apiService.getWorkoutPackageConsumption(workout.id);
            consumptions[workout.id] = consumption;
          } catch (error) {
            console.error(`Error loading package consumption for workout ${workout.id}:`, error);
            consumptions[workout.id] = [];
          }
        }

        setPackageConsumptions(consumptions);
      }
    };

    loadPackageConsumptions();
  }, [workouts]);

  // Функция для проверки, списывается ли пакет для клиента в тренировке
  const hasPackageConsumption = (workoutId: number, clientId: number): boolean => {
    const consumption = packageConsumptions[workoutId];
    if (!consumption || consumption.length === 0) return false;

    return consumption.some(c => c.client_id === clientId && c.has_package);
  };

  // Функция для открытия модального окна клиента
  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setIsViewClientModalOpen(true);
  };

  // Форматирование даты
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const weekdays = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
      const weekday = weekdays[date.getDay()];
      return `${weekday} ${format(date, 'dd MMMM yyyy', { locale: ru })}`;
    } catch {
      return 'Неверная дата';
    }
  };

  // Форматирование даты для мобильных (более компактно)
  const formatDateMobile = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const weekdays = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
      const weekday = weekdays[date.getDay()];
      return `${weekday} ${format(date, 'dd MMM yyyy', { locale: ru })}`;
    } catch {
      return 'Неверная дата';
    }
  };

  // Форматирование времени
  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'HH:mm', { locale: ru });
    } catch {
      return 'Неверное время';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Навигационная панель */}
      <Navigation currentPage="workouts" />

      {/* Заголовок страницы */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Тренировки</h1>
              <p className="mt-1 text-sm text-gray-500">
                Управление тренировками и их расписанием
              </p>
            </div>
            <button
              onClick={() => setIsCreateWorkoutModalOpen(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <Plus size={16} className="mr-2" />
              Создать тренировку
            </button>
          </div>
        </div>
      </div>

      {/* Основной контент */}
      <main className="max-w-7xl mx-auto py-4 sm:px-6 lg:px-8">
        <div className="px-4 py-2 sm:px-0">

          {/* Фильтры и поиск */}
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="flex flex-col gap-4">
              {/* Поиск */}
              <div className="w-full">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Поиск тренировок или клиентов..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Очистить поиск"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Фильтр по статусу */}
              <div className="w-full sm:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'completed' | 'pending')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Все тренировки</option>
                  <option value="pending">Запланированные</option>
                  <option value="completed">Завершенные</option>
                </select>
              </div>
            </div>

            {/* Статистика */}
            <div className="text-sm text-gray-500 flex items-center justify-end">
              {searchTerm ? (
                `Показано ${workouts.length} из ${totalCount} результатов поиска`
              ) : (
                `Показано ${workouts.length} из ${totalCount} тренировок`
              )}
            </div>
          </div>

          {/* Пагинация сверху */}
          {totalPages > 1 && (
            <div className="mb-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}

          {/* Список тренировок */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Загрузка тренировок...</p>
            </div>
          ) : workouts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Calendar size={48} className="mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {totalCount === 0 ? 'Тренировок пока нет' : 'Тренировки не найдены'}
              </h3>
              <p className="text-gray-500 mb-4">
                {totalCount === 0 
                  ? 'Создайте первую тренировку, чтобы начать работу'
                  : 'Попробуйте изменить параметры поиска или фильтры'
                }
              </p>
              {totalCount === 0 && (
                <button
                  onClick={() => setIsCreateWorkoutModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Создать тренировку
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="space-y-2">
                {workouts.map((workout) => (
                  <li key={workout.id} className="bg-white rounded-lg shadow border border-gray-100">
                    <div className="px-3 py-3 sm:px-6 sm:py-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-2 sm:space-y-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between space-x-3">
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3 flex-1 min-w-0">
                              <Link
                                to={`/workouts/${workout.id}`}
                                className="text-base sm:text-lg font-medium text-blue-600 hover:text-blue-800 truncate cursor-pointer"
                              >
                                {workout.name}
                              </Link>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit ${
                                workout.is_completed 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {workout.is_completed ? 'Завершена' : 'Запланирована'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {!workout.is_completed && (
                                <button
                                  onClick={() => handleCompleteWorkout(workout.id)}
                                  disabled={isCompleteLoading}
                                  className="text-green-600 hover:text-green-800 p-2 disabled:opacity-50"
                                  title="Завершить тренировку"
                                >
                                  <CheckCircle size={18} />
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/workouts/${workout.id}`)}
                                className="text-blue-600 hover:text-blue-800 p-2"
                                title="Просмотр"
                              >
                                <Eye size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteWorkout(workout.id)}
                                disabled={isDeleteLoading}
                                className="text-red-600 hover:text-red-800 p-2 disabled:opacity-50"
                                title="Удалить"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                          
                          {workout.description && (
                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                              {workout.description}
                            </p>
                          )}
                          
                          <div className="mt-2 space-y-1 sm:space-y-2">
                            {/* Дата и время - оптимизировано для мобильных */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 text-xs sm:text-sm text-gray-500">
                              <div className="flex items-center mb-1 sm:mb-0">
                                <Calendar size={12} className="mr-1 flex-shrink-0 sm:w-3.5 sm:h-3.5" />
                                <span className="font-medium">
                                  <span className="hidden sm:inline">{formatDate(workout.date)}</span>
                                  <span className="sm:hidden">{formatDateMobile(workout.date)}</span>
                                </span>
                              </div>
                              <div className="flex items-center">
                                <Clock size={12} className="mr-1 flex-shrink-0 sm:w-3.5 sm:h-3.5" />
                                <span className="font-medium">{formatTime(workout.date)}</span>
                                {workout.duration_minutes && (
                                  <span className="ml-1 text-gray-400">({workout.duration_minutes} мин)</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Клиенты - отдельная строка для лучшей читаемости */}
                            {workout.clients && workout.clients.length > 0 && (
                              <div className="flex items-center">
                                <Users size={12} className="mr-1 flex-shrink-0 text-gray-500 sm:w-3.5 sm:h-3.5" />
                                <div className="flex flex-wrap gap-1">
                                  {workout.clients?.map((client) => {
                                    // Подсчитываем количество упражнений для конкретного клиента
                                    const clientExerciseCount = workout.workout_exercises?.reduce((count, workoutExercise) => {
                                      // Проверяем, есть ли логи выполнения для этого клиента в данном упражнении
                                      const hasClientLogs = workoutExercise.exercise_logs?.some(log => log.client_id === client.id);
                                      return hasClientLogs ? count + 1 : count;
                                    }, 0) || 0;
                                    
                                    // Проверяем, есть ли shared link для этого клиента в этой тренировке
                                    const hasSharedLink = workout.shared_exercises?.some(
                                      shared => shared.client_id === client.id && shared.is_active
                                    );
                                    
                                    const hasPackage = hasPackageConsumption(workout.id, client.id);

                                    return (
                                      <span
                                        key={client.id}
                                        onClick={() => handleClientClick(client)}
                                        title={hasPackage
                                          ? `Тренировка списывается из пакета`
                                          : `Тренировка НЕ списывается из пакета`
                                        }
                                        className={`inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                                          hasPackage
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-orange-100 text-orange-800'
                                        }`}
                                      >
                                        {client.first_name} {client.last_name}
                                        <span className={`ml-1 ${
                                          hasPackage ? 'text-blue-800' : 'text-orange-800'
                                        }`}>
                                          ({clientExerciseCount})
                                        </span>
                                        {hasSharedLink && (
                                          <div title="Есть поделившаяся ссылка">
                                            <Share2 size={12} className={`ml-1 ${
                                              hasPackage ? 'text-blue-800' : 'text-orange-800'
                                            }`} />
                                          </div>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        
        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="py-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
        </div>
      </main>

      {/* Модальное окно создания тренировки */}
      <CreateWorkoutModal
        isOpen={isCreateWorkoutModalOpen}
        onClose={() => setIsCreateWorkoutModalOpen(false)}
        onSubmit={handleCreateWorkout}
        isLoading={isCreateWorkoutLoading}
      />

      {/* Модальное окно просмотра клиента */}
      <ViewClientModal
        isOpen={isViewClientModalOpen}
        onClose={() => {
          setIsViewClientModalOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onWeightClick={() => {
          if (selectedClient) {
            setSelectedClientForWeight(selectedClient);
            setIsWeightModalOpen(true);
          }
        }}
        onMeasurementsClick={() => {
          if (selectedClient) {
            setSelectedClientForBodyMeasurements(selectedClient);
            setIsBodyMeasurementsModalOpen(true);
          }
        }}
        onPhotoClick={() => {
          if (selectedClient) {
            setSelectedClientForPhoto(selectedClient);
            setIsPhotoModalOpen(true);
          }
        }}
        onStatsClick={() => {
          if (selectedClient) {
            navigate(`/clients/${selectedClient.id}/stats`);
          }
        }}
        onNutritionClick={() => {
          if (selectedClient) {
            navigate(`/clients/${selectedClient.id}/nutrition`);
          }
        }}
        onWorkoutClick={() => {
          if (selectedClient) {
            setIsCreateWorkoutModalOpen(true);
          }
        }}
      />

      {/* Модальное окно веса */}
      <WeightModal
        isOpen={isWeightModalOpen}
        onClose={() => { setIsWeightModalOpen(false); setSelectedClientForWeight(null); }}
        clientId={selectedClientForWeight?.id || 0}
      />

      {/* Модальное окно замеров */}
      <BodyMeasurementsModal
        isOpen={isBodyMeasurementsModalOpen}
        onClose={() => { setIsBodyMeasurementsModalOpen(false); setSelectedClientForBodyMeasurements(null); }}
        clientId={selectedClientForBodyMeasurements?.id || 0}
      />

      {/* Модальное окно фото */}
      <ClientPhotoModal
        isOpen={isPhotoModalOpen}
        onClose={() => { setIsPhotoModalOpen(false); setSelectedClientForPhoto(null); }}
        client={selectedClientForPhoto as Client}
      />
    </div>
  );
};

export default WorkoutsPage; 